import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  message,
  Space,
  Modal,
  Form,
  Input,
  Tooltip,
  Row,
  Tag,
  Empty
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined, 
  UndoOutlined,
  ExclamationCircleFilled
} from '@ant-design/icons';
import { membershipService } from '../../../services';
import {ExtendedCustomProductResponse} from "../../../model/props/changeFlag";
import {
  CustomProductsTabProps,
  MembershipCustomProductCreateItem,
  MembershipCustomProductDeleteItem,
  MembershipCustomProductUpdateItem
} from "../../../model";

const { confirm } = Modal;



const CustomProductsTab: React.FC<CustomProductsTabProps> = ({
  membershipId,
  onDataChanged,
  changedFields,
  onSaveSuccess,
  isEditing,
}) => {
  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState<ExtendedCustomProductResponse[]>([]);
  const [productData, setProductData] = useState<ExtendedCustomProductResponse[]>([]);
  const [modifiedProducts, setModifiedProducts] = useState<{[key: number]: boolean}>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<ExtendedCustomProductResponse | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const userAccount = localStorage.getItem('userAccount') ?? '';

  // 載入自訂產品資料
  const fetchCustomProductData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await membershipService.getCustomProducts({ membershipId });
      if (data && Array.isArray(data)) {
        setProductData(data);
        setOriginalData(JSON.parse(JSON.stringify(data))); // 深拷貝保存原始資料
        setModifiedProducts({});
        setHasChanges(false);
        // 通知父元件已無變更
        onDataChanged('customProducts', false);
      } else {
        setProductData([]);
        setOriginalData([]);
        message.info('沒有找到自訂產品資料');
      }
    } catch (error) {
      console.error('Failed to fetch custom product data:', error);
      message.error('載入自訂產品資料時發生錯誤');
      setProductData([]);
      setOriginalData([]);
    } finally {
      setLoading(false);
    }
  }, [membershipId, onDataChanged]);

  useEffect(() => {
    fetchCustomProductData();
  }, [fetchCustomProductData]);

  // 判斷產品是否已修改
  const isProductModified = (record: ExtendedCustomProductResponse) => {
    return !!modifiedProducts[record.membershipCustomerProductId];
  };
  
  // 產品是否被標記為刪除
  const isProductDeleted = (record: ExtendedCustomProductResponse) => {
    return !!record._deleted;
  };
  
  // 產品是否為新增
  const isProductNew = (record: ExtendedCustomProductResponse) => {
    return record.membershipCustomerProductId < 0 || !!record._new;
  };

  // 新增自訂產品
  const handleAddProduct = async () => {
    if (!isEditing) return;
    
    try {
      const values = await addForm.validateFields();
      
      // 檢查是否有重複產品名稱
      const isDuplicate = productData.some(item => 
        item.productName === values.productName && !item._deleted
      );
      
      if (isDuplicate) {
        message.error('此產品名稱已存在，請輸入不同的產品名稱');
        return;
      }
      
      // 將新產品加入至暫存資料中
      const newProduct: ExtendedCustomProductResponse = {
        membershipCustomerProductId: -Date.now(), // 臨時负值 ID，用來識別新增項目
        productName: values.productName,
        productNameEn: values.productNameEn || '',
        _new: true
      };
      
      const newData = [...productData, newProduct];
      setProductData(newData);
      
      // 標記為已修改
      setModifiedProducts(prev => ({
        ...prev,
        [newProduct.membershipCustomerProductId]: true
      }));
      setHasChanges(true);
      
      // 通知上層組件有資料變更
      onDataChanged('customProducts', true);
      
      // setIsAddModalVisible(false);
      addForm.resetFields();
      message.success('已添加自訂產品，點擊儲存按鈕以儲存變更');
    } catch (error) {
      console.error('Add custom product failed:', error);
      message.error('新增自訂產品時發生錯誤');
    }
  };

  // 更新自訂產品
  const handleUpdateProduct = async () => {
    if (!currentProduct || !isEditing) return;
    
    try {
      const values = await editForm.validateFields();
      
      // 檢查是否有重複產品名稱
      const isDuplicate = productData.some(item => 
        item.productName === values.productName && 
        item.membershipCustomerProductId !== currentProduct.membershipCustomerProductId &&
        !item._deleted
      );
      
      if (isDuplicate) {
        message.error('此產品名稱已存在，請輸入不同的產品名稱');
        return;
      }
      
      // 在暫存資料中更新產品
      const newData = productData.map(item => {
        if (item.membershipCustomerProductId === currentProduct.membershipCustomerProductId) {
          return {
            ...item,
            productName: values.productName,
            productNameEn: values.productNameEn || ''
          };
        }
        return item;
      });
      
      setProductData(newData);
      
      // 標記為已修改
      setModifiedProducts(prev => ({
        ...prev,
        [currentProduct.membershipCustomerProductId]: true
      }));
      setHasChanges(true);
      
      // 通知上層組件有資料變更
      onDataChanged('customProducts', true);
      
      setIsEditModalVisible(false);
      setCurrentProduct(null);
      editForm.resetFields();
      message.success('已更新自訂產品，點擊儲存按鈕以儲存變更');
    } catch (error) {
      console.error('Update custom product failed:', error);
      message.error('更新自訂產品時發生錯誤');
    }
  };

  // 刪除自訂產品
  const handleDeleteProduct = (record: ExtendedCustomProductResponse) => {
    if (!isEditing) return;
    
    // 如果是新增的項目（负數 ID）
    if (record.membershipCustomerProductId < 0) {
      // 直接從暫存資料中移除
      setProductData(productData.filter(item => 
        item.membershipCustomerProductId !== record.membershipCustomerProductId
      ));
      
      // 從修改記錄中移除
      const updatedModified = { ...modifiedProducts };
      delete updatedModified[record.membershipCustomerProductId];
      setModifiedProducts(updatedModified);
      
      // 更新變更狀態
      const hasRemainingChanges = Object.keys(updatedModified).length > 0;
      setHasChanges(hasRemainingChanges);
      onDataChanged('customProducts', hasRemainingChanges);
      
      message.success('已移除自訂產品');
      return;
    }
    
    // 將現有產品標記為待刪除
    setProductData(prevData => 
      prevData.map(item => 
        item.membershipCustomerProductId === record.membershipCustomerProductId
          ? { ...item, _deleted: true }
          : item
      )
    );
    
    // 標記為已修改
    setModifiedProducts(prev => ({
      ...prev,
      [record.membershipCustomerProductId]: true
    }));
    setHasChanges(true);
    
    // 通知上層組件有資料變更
    onDataChanged('customProducts', true);
    
    message.success('已標記自訂產品為刪除，點擊儲存按鈕以完成刪除');
  };

  // 確認刪除自訂產品
  const confirmDelete = (record: ExtendedCustomProductResponse) => {
    if (!isEditing) return;
    
    confirm({
      title: '確定要刪除此自訂產品嗎？',
      icon: <ExclamationCircleFilled />,
      content: `將會移除自訂產品：${record.productName}`,
      okText: '確定',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        handleDeleteProduct(record);
      },
    });
  };

  // 開啟編輯視窗
  const showEditModal = (record: ExtendedCustomProductResponse) => {
    if (!isEditing) return;
    
    setCurrentProduct(record);
    editForm.setFieldsValue({
      productName: record.productName,
      productNameEn: record.productNameEn
    });
    setIsEditModalVisible(true);
  };
  
  // 儲存所有變更
  const handleSaveChanges = async () => {
    if (!hasChanges || !isEditing) {
      message.info('沒有需要儲存的變更');
      return;
    }
    
    setLoading(true);
    
    try {
      // 備份原本資料，用於失敗時還原
      const originalProductDataBackup = JSON.parse(JSON.stringify(productData));
      
      // 准備要新增的自訂產品
      const productsToAdd = productData
        .filter(p => p.membershipCustomerProductId < 0 || p._new) // 新增的產品有臨時负值ID或_new標記
        .map(p => ({
          membershipId,
          productName: p.productName,
          productNameEn: p.productNameEn,
          createdBy: userAccount
        }) as MembershipCustomProductCreateItem);
      
      // 准備要更新的自訂產品
      const productsToUpdate = productData
        .filter(p => p.membershipCustomerProductId > 0 && // 正常 ID
                  modifiedProducts[p.membershipCustomerProductId] && // 已修改 
                  !p._deleted && // 不是待刪除
                  !p._new) // 不是新增項目
        .map(p => ({
          membershipCustomPorductId: p.membershipCustomerProductId,
          membershipId,
          productName: p.productName,
          productNameEn: p.productNameEn,
          modifiedBy: userAccount
        }) as MembershipCustomProductUpdateItem);
      
      // 准備要刪除的自訂產品
      const productsToDelete = productData
        .filter(p => p.membershipCustomerProductId > 0 && p._deleted) // 被標記為刪除的現有產品
        .map(p => ({
          membershipCustomPorductId: p.membershipCustomerProductId
        }) as MembershipCustomProductDeleteItem);
      
      // 执行 API 請求
      let isSuccess = true;
      let errorMsg = '';
      
      // 新增自訂產品
      if (productsToAdd.length > 0) {
        try {
          const createResponse = await membershipService.createCustomProducts({
            membershipCustomProducts: productsToAdd
          });
          
          if (!createResponse || createResponse.createdCount === 0) {
            isSuccess = false;
            errorMsg = '新增自訂產品失敗';
          }
        } catch (error) {
          console.error('Create custom products failed:', error);
          isSuccess = false;
          errorMsg = '新增自訂產品時發生錯誤';
        }
      }
      
      // 更新自訂產品
      if (isSuccess && productsToUpdate.length > 0) {
        try {
          const updateResponse = await membershipService.updateCustomProducts({
            membershipCustomProducts: productsToUpdate
          });
          
          if (!updateResponse || updateResponse.updatedCount === 0) {
            isSuccess = false;
            errorMsg = '更新自訂產品失敗';
          }
        } catch (error) {
          console.error('Update custom products failed:', error);
          isSuccess = false;
          errorMsg = '更新自訂產品時發生錯誤';
        }
      }
      
      // 刪除自訂產品
      if (isSuccess && productsToDelete.length > 0) {
        try {
          const deleteResponse = await membershipService.deleteCustomProducts({
            membershipCustomPorductIds: productsToDelete,
            modifiedBy: userAccount
          });
          
          if (!deleteResponse || deleteResponse.deletedCount === 0) {
            isSuccess = false;
            errorMsg = '刪除自訂產品失敗';
          }
        } catch (error) {
          console.error('Delete custom products failed:', error);
          isSuccess = false;
          errorMsg = '刪除自訂產品時發生錯誤';
        }
      }
      
      if (isSuccess) {
        message.success('所有自訂產品變更已儲存成功');
        // 重新載入最新資料
        fetchCustomProductData();
        onSaveSuccess();
      } else {
        // 還原原始資料
        setProductData(originalProductDataBackup);
        message.error(errorMsg || '儲存自訂產品變更失敗');
      }
    } catch (error) {
      console.error('Save custom products failed:', error);
      message.error('儲存自訂產品變更時發生錯誤');
      // 還原原始資料
      handleResetChanges();
    } finally {
      setLoading(false);
    }
  };
  
  // 重置變更
  const handleResetChanges = () => {
    setProductData(JSON.parse(JSON.stringify(originalData)));
    setModifiedProducts({});
    setHasChanges(false);
    onDataChanged('customProducts', false);
    message.info('已返回原始資料');
  };

  // 獲取表格欄位定義
  const getColumns = () => {
    // 基本欄位
    const baseColumns = [
      {
        title: '工廠登記生產之產品',
        dataIndex: 'productName',
        key: 'productName',
        render: (text: string, record: ExtendedCustomProductResponse) => {
          if (isProductDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text}</span>;
          }
          return isProductModified(record) ? 
            <span style={{ color: '#f50' }}>{text}</span> : 
            text;
        }
      },
      {
        title: '產品英文名稱',
        dataIndex: 'productNameEn',
        key: 'productNameEn',
        render: (text: string, record: ExtendedCustomProductResponse) => {
          if (isProductDeleted(record)) {
            return <span style={{ textDecoration: 'line-through', color: '#999' }}>{text || '-'}</span>;
          }
          return isProductModified(record) ? 
            <span style={{ color: '#f50' }}>{text || '-'}</span> : 
            (text || '-');
        }
      }
    ];
    
    // 只在編輯模式下添加額外的欄位
    if (isEditing) {
      return [
        ...baseColumns,
        {
          title: '狀態',
          key: 'status',
          width: 100,
          render: (_: any, record: ExtendedCustomProductResponse) => {
            if (isProductDeleted(record)) {
              return <Tag color="red">待刪除</Tag>;
            }
            if (isProductNew(record)) {
              return <Tag color="green">新增</Tag>;
            }
            if (isProductModified(record)) {
              return <Tag color="orange">已修改</Tag>;
            }
            return null;
          }
        },
        {
          title: '操作',
          key: 'action',
          width: 120,
          render: (_: any, record: ExtendedCustomProductResponse) => {
            if (isProductDeleted(record)) {
              return (
                <Button 
                  type="link" 
                  onClick={() => {
                    // 取消刪除標記
                    setProductData(prevData => 
                      prevData.map(item => 
                        item.membershipCustomerProductId === record.membershipCustomerProductId
                          ? { ...item, _deleted: false }
                          : item
                      )
                    );
                    // 使用時間戳創建新的修改記錄，以確保觸發狀態更新
                    setModifiedProducts(prev => {
                      const newModified = { ...prev };
                      delete newModified[record.membershipCustomerProductId];
                      return newModified;
                    });
                    // 更新變更狀態
                    setHasChanges(true);
                    onDataChanged('customProducts', true);
                  }}
                >
                  取消刪除
                </Button>
              );
            }
            
            return (
              <Space size="small">
                <Tooltip title="編輯">
                  <Button 
                    type="text" 
                    icon={<EditOutlined />} 
                    onClick={() => showEditModal(record)} 
                    disabled={isProductDeleted(record)}
                  />
                </Tooltip>
                <Tooltip title="刪除">
                  <Button 
                    type="text" 
                    icon={<DeleteOutlined />} 
                    danger 
                    onClick={() => confirmDelete(record)}
                    disabled={isProductDeleted(record)}
                  />
                </Tooltip>
              </Space>
            );
          },
        }
      ];
    }
    
    return baseColumns;
  };

  const renderTable = () => {
    if (!productData || productData.length === 0) {
      return (
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
          description="尚無自訂產品資料" 
        />
      );
    }
    
    return (
      <Table
        sticky
        rowKey="membershipCustomerProductId"
        columns={getColumns()}
        dataSource={productData.filter(item => !isProductDeleted(item) || isEditing)}
        loading={loading}
        pagination={false}
        rowClassName={(record) => isProductModified(record) ? 'modified-row' : ''}
      />
    );
  };

  return (
    <Card>
      {isEditing && (
        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => setIsAddModalVisible(true)}
            >
              新增自訂產品
            </Button>
          </Space>
          
          <Space>
            <Button 
              icon={<UndoOutlined />} 
              onClick={handleResetChanges}
              disabled={!hasChanges}
            >
              重置
            </Button>
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={handleSaveChanges}
              loading={loading}
              disabled={!hasChanges}
            >
              儲存變更
            </Button>
          </Space>
        </Row>
      )}

      {renderTable()}

      {/* 新增自訂產品視窗 */}
      <Modal
        title="新增自訂產品"
        open={isAddModalVisible}
        onOk={handleAddProduct}
        onCancel={() => {
          setIsAddModalVisible(false);
          addForm.resetFields();
        }}
        okText="新增"
        cancelText="關閉"
        confirmLoading={loading}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="productName"
            label="產品名稱"
            rules={[{ required: true, message: '請輸入產品名稱' }]}
          >
            <Input placeholder="請輸入產品名稱" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="productNameEn"
            label="產品英文名稱"
          >
            <Input placeholder="請輸入產品英文名稱（選填）" maxLength={150} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 編輯自訂產品視窗 */}
      <Modal
        title="編輯自訂產品"
        open={isEditModalVisible}
        onOk={handleUpdateProduct}
        onCancel={() => {
          setIsEditModalVisible(false);
          setCurrentProduct(null);
          editForm.resetFields();
        }}
        okText="更新"
        cancelText="取消"
        confirmLoading={loading}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="productName"
            label="產品名稱"
            rules={[{ required: true, message: '請輸入產品名稱' }]}
          >
            <Input placeholder="請輸入產品名稱" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="productNameEn"
            label="產品英文名稱"
          >
            <Input placeholder="請輸入產品英文名稱（選填）" maxLength={150} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CustomProductsTab;
